import type { ActionEntity } from 'actions';
import { isNodeTypeDeprecated, type NodeType } from 'redux/slices/lists/nodeTypesSlice';
import { type WorkspaceItem } from 'api/workspace/endpoints/getWorkspaceContents';

/**
 * Project a merged-contents row to the action registry's `ActionEntity` so a
 * workspace row reuses the exact same actions as its list page. A per-type
 * switch, NOT a uniform spread: the variants differ (`table` carries its display
 * name as `tableName` rather than `name`; integration / report_definition /
 * monitoring_dashboard / python_library omit zoneName; several are id-only), and
 * node_definition's `isDeprecated` is derived from its `nodeType` against the
 * loaded `/lists/node_types` LOV (the backend emits no deprecation flag).
 *
 * Returns null for a `zone` row — zones are tree roots, not actionable contents.
 */
export const toActionEntity = (item: WorkspaceItem, knownNodeTypes: NodeType[]): ActionEntity | null => {
  const zoneName = item.zoneName ?? '';
  switch (item.type) {
    case 'zone':
      return null;
    case 'folder':
      return { type: 'folder', id: item.id, name: item.name };
    case 'integration':
      return { type: 'integration', id: item.id, name: item.name };
    case 'python_library':
      return { type: 'python_library', id: item.id, name: item.name };
    case 'report_definition':
      return { type: 'report_definition', id: item.id };
    case 'monitoring_dashboard':
      return { type: 'monitoring_dashboard', id: item.id };
    case 'lock':
      return { type: 'lock', id: item.id, zoneName, name: item.name };
    case 'trigger':
      return { type: 'trigger', id: item.id, zoneName, name: item.name };
    case 'scheduling_window':
      return { type: 'scheduling_window', id: item.id, zoneName, name: item.name };
    case 'recurrence_definition':
      return { type: 'recurrence_definition', id: item.id, zoneName, name: item.name };
    case 'queue':
      return { type: 'queue', id: item.id, zoneName, name: item.name, status: item.status };
    case 'event':
      return { type: 'event', id: item.id, zoneName, name: item.name, eventType: item.eventType };
    case 'connection':
      return { type: 'connection', id: item.id, name: item.name, connectionType: item.connectionType };
    case 'node_definition':
      return { type: 'node_definition', id: item.id, zoneName, name: item.name, isDeprecated: isNodeTypeDeprecated(item.nodeType, knownNodeTypes) };
    case 'table':
      return { type: 'table', id: item.id, zoneName, tableName: item.name };
  }
};
